import { Suspense, useState, useEffect, useContext } from 'react'
import Header from './furniture/Header'
import Breadcrumbs from './furniture/Breadcrumbs'
import Footer from './furniture/Footer'
import Loader from './helpers/Loader'
import Alert from './furniture/Alert'
import Skip from './furniture/Skip'

import Accordion from './sections/Accordion'
import BasicContent from './sections/BasicContent'
import BasicContentWithColumns from './sections/BasicContentWithColumns'
import CalloutCard from './sections/CalloutCard'
import CardListCurated from './sections/CardListCurated'
import CardListDynamic from './sections/CardListDynamic'
import ContentFiftybyFifty from './sections/ContentFiftybyFifty'
import HeroBanner from './sections/HeroBanner'
import InformationBanner from './sections/InformationBanner'
import InformationBox from './sections/InformationBox'
import RatesCalculator from './sections/RatesCalculator'
import IframeEmbed from './sections/IframeEmbed'
import RatesCalloutCard from './sections/RatesCalloutCard'
import RatesCardList from './sections/RatesCardList'
import SearchResult from './sections/SearchResult'
import SubPageBanner from './sections/SubPageBanner'
import ThirdParty from './sections/ThirdParty'
import { showExternalLinkIcons } from '../helpers/style'
import RatesCalloutBanner from './sections/RatesCalloutBanner'
import ProductFiftyFiftyContent from './sections/ProductFiftyFiftyContent'
import TagListing from './sections/TagListing'
import TestimonialCarousel from './sections/TestimonialCarousel'
import StepsCards from './sections/StepsCards'
import ArticleHeader from './sections/ArticleHeader'
import ArticleContainer from './sections/ArticleContainer'
import MemberAreaContainer from './sections/MemberAreaContainer'
import SectionHeader from './sections/SectionHeader'
import JumpToSection from './sections/JumpToSection'
import EventKeyDetails from './sections/EventKeyDetails'
import WayFinder from './sections/WayFinder'
import RegistrationForm from './sections/RegistrationForm'
import RenewalsEmbedForm from './sections/RenewalsEmbedForm'
import MemberSelfServiceForms from './sections/MemberSelfServiceForms'
import RenewButton from './sections/RenewButton'
import InvoicePage from './sections/InvoicePage'
import PaymentFormPage from './sections/PaymentFormPage'
import PendingApprovalPage from './sections/PendingApprovalPage'
import ReceiptDownloadPage from './sections/ReceiptDownloadPage'
import {
  getComponentNameAndData,
  getComponentAnchorId,
  shouldUseEventKeySidebarLayout,
  partitionForEventKeySidebar,
} from '../helpers/pageComponentLayout'
import { JourneyGateContext } from '../providers/JourneyGate'

function Page(props) {
  const [ComponentsList, setComponentsList] = useState([])
  // While a journey status check may still redirect the member, hide the page alerts and
  // every block other than the gate source (or the container that nests it).
  const { gateActive, gateSource, gateNested } = useContext(JourneyGateContext)
  const [menuActive, setMenuActive] = useState(false)
  const [searchActive, setSearchActive] = useState(false)
  const [ctasActive, setCtasActive] = useState(false)

  const deskLogo = props.nav?.siteLogo ?? ''
  const mobLogo = props.nav?.siteLogoMobile ?? ''

  // Per-page "Hide breadcrumbs" toggle (pageBase.hideBreadcrumbs). When on, suppress the
  // breadcrumb trail wherever it renders (after the section header and inside the article header).
  const breadcrumbs = props.res?.hideBreadcrumbs ? [] : props.breadcrumbs

  const components = {
    accordion: Accordion,
    basicContent: BasicContent,
    basicContentWithColumns: BasicContentWithColumns,
    calloutCard: CalloutCard,
    cardListCurated: CardListCurated,
    cardListDynamic: CardListDynamic,
    contentFiftybyFifty: ContentFiftybyFifty,
    heroBanner: HeroBanner,
    informationBanner: InformationBanner,
    informationBox: InformationBox,
    ratesCalculator: RatesCalculator,
    iframeEmbed: IframeEmbed,
    ratesCalloutCard: RatesCalloutCard,
    ratesCardList: RatesCardList,
    searchResult: SearchResult,
    subPageBanner: SubPageBanner,
    thirdParty: ThirdParty,
    ratesCalloutBanner: RatesCalloutBanner,
    productFiftyFiftyContent: ProductFiftyFiftyContent,
    tagListing: TagListing,
    testimonials: TestimonialCarousel,
    stepsCards: StepsCards,
    articleHeader: ArticleHeader,
    articleContainer: ArticleContainer,
    memberAreaContainer: MemberAreaContainer,
    sectionHeader: SectionHeader,
    jumpToSection: JumpToSection,
    eventKeyDetails: EventKeyDetails,
    wayfinderComponent: WayFinder,
    registrationComponent: RegistrationForm,
    renewalsEmbedForm: RenewalsEmbedForm,
    memberSelfServiceForms: MemberSelfServiceForms,
    renewButton: RenewButton,
    invoicePage: InvoicePage,
    paymentFormPage: PaymentFormPage,
    pendingApprovalPage: PendingApprovalPage,
    receiptDownloadPage: ReceiptDownloadPage,
  }

  const noDataExcludeList = [
    // a list of items that don't require data, and will not trigger the warning and be ignored if the data object does not exist.
    // there should not be too many of these.
    'thirdParty',
    // Merges from current page; payload may be empty after `key` is stripped when not on an event document type.
    'eventKeyDetails',
  ]

  useEffect(() => {
    showExternalLinkIcons()
  }, [])

  useEffect(() => {
    if (props.content?.length) {
      setComponentsList(() => {
        // This only loads once, on page load, so we don't need to worry about the content of prevState
        const newState = []
        if (!props.content.some((component) => component.componentName == 'thirdParty')) {
          props.content.push({
            componentName: 'thirdParty',
            componentData: {
              key: 'thirdParty',
            },
          })
        }

        props.content.forEach((comp) => {
          const { typeKey, payload, jumpToId } = getComponentNameAndData(comp)
          if (payload) {
            newState.push({
              name: typeKey,
              data: payload,
              jumpToId,
            })
            return
          }
          console.warn(`No data sent for component ${typeKey ?? '(unknown)'}`)
        })

        // Log components list in development mode
        if (process.env.NODE_ENV === 'development') {
          console.log(
            '📦 Components on this page:',
            newState.map((comp) => comp.name),
          )
          console.table(
            newState.map((comp) => ({
              Component: comp.name,
              Key: comp.data?.key || 'N/A',
              HasData: Object.keys(comp.data || {}).length > 0,
            })),
          )
        }

        return [...newState]
      })
    }
  }, [props.content])

  return (
    <>
      <Skip />
      <Header
        deskLogo={deskLogo}
        mobLogo={mobLogo}
        ctas={props.nav?.utilityLinks}
        menuActive={menuActive}
        setMenuActive={setMenuActive}
        searchActive={searchActive}
        setSearchActive={setSearchActive}
        ctasActive={ctasActive}
        setCtasActive={setCtasActive}
        nav={props.nav?.items}
        headerDataSource={props.nav?.headerDataSource}
        res={props.res}
      >
        {!gateActive &&
          props.res.globalEnablePageAlert &&
          (props.res.globalPageAlertTitle || props.res.globalPageAlertDescription) && (
            <Alert
              title={props.res.globalPageAlertTitle}
              type={props.res.globalPageAlertType}
              description={props.res.globalPageAlertDescription}
            />
          )}
        {!gateActive && props.res.enablePageAlert && (props.res.pageAlertTitle || props.res.pageAlertDescription) && (
          <Alert
            title={props.res.pageAlertTitle}
            type={props.res.pageAlertType}
            description={props.res.pageAlertDescription}
          />
        )}
      </Header>
      <main id="main">
        <Suspense key={'pageSuspender'} fallback={<Loader />}>
          <div data-jump-scan-root>
            {ComponentsList &&
              ComponentsList.length > 0 &&
              (() => {
                const { leading, main, sidebar, hasSidebar } = partitionForEventKeySidebar(ComponentsList)

                const renderBlock = ({ comp, idx }) => {
                  const { typeKey, payload, jumpToId } = getComponentNameAndData(comp)
                  const TagName = typeKey ? components[typeKey] : undefined
                  const rowKey = payload?.key ?? typeKey ?? idx
                  if (!TagName) {
                    console.warn(`Component type ${typeKey ?? '(unknown)'} was not found. Ignoring...`)
                    return <span key={rowKey} />
                  }
                  if (!Object.keys(payload ?? {}).length && !noDataExcludeList.includes(typeKey)) {
                    console.warn(`No data for component type ${typeKey} was found. Ignoring...`)
                    return <span key={rowKey} />
                  }
                  const compData = { ...payload }
                  const blockKey = compData.key
                  delete compData.key
                  if (typeKey === 'articleHeader' || typeKey === 'sectionHeader') {
                    compData.breadcrumbs = breadcrumbs
                  }
                  const fromApi = typeof jumpToId === 'string' && jumpToId.trim().length > 0 ? jumpToId.trim() : null
                  const componentAnchorId = fromApi ?? getComponentAnchorId(blockKey, idx)
                  // CSS-hide (not unmount) siblings while the gate is closed: embedded D365 forms
                  // need their [data-form-id] nodes in the DOM when FormLoader loads. The gate
                  // source stays visible, as does the memberAreaContainer nesting a gate source.
                  const gateHidden =
                    gateActive && typeKey !== gateSource && !(gateNested && typeKey === 'memberAreaContainer')
                  return (
                    <div key={rowKey} id={componentAnchorId} className={`scroll-mt-24${gateHidden ? ' hidden' : ''}`}>
                      <TagName {...compData} idx={idx} />
                      {comp.name === 'sectionHeader' && (
                        <Breadcrumbs items={breadcrumbs} noBorder hideBelowLg />
                      )}
                    </div>
                  )
                }

                const useEventSidebarLayout = shouldUseEventKeySidebarLayout(props.res, hasSidebar, leading.length)

                if (!useEventSidebarLayout) {
                  return ComponentsList.map((comp, idx) => renderBlock({ comp, idx }))
                }

                return (
                  <>
                    {leading.map(({ comp, idx }) => renderBlock({ comp, idx }))}
                    <div className="container mx-auto px-5 lg:px-0 lg:pb-12 lg:pt-6 xl:pt-8">
                      <div className="flex flex-col gap-10 lg:grid lg:grid-cols-[minmax(0,1fr)_308px] lg:items-start lg:gap-x-8 xl:gap-x-10">
                        <div className="flex min-w-0 flex-col gap-10">{main.map(renderBlock)}</div>
                        <div className="event-key-details-sidebar flex w-full shrink-0 flex-col gap-6 lg:sticky lg:top-24 lg:w-[308px] lg:self-start xl:top-28">
                          {sidebar.map(renderBlock)}
                        </div>
                      </div>
                    </div>
                  </>
                )
              })()}
          </div>
        </Suspense>
      </main>
      {!props.res.disablePrimaryFooter && (
        <Footer
          {...props.footer}
          deskLogo={deskLogo}
          mobLogo={mobLogo}
          disableSticky={props.res.disableUtilityFooter}
          stickyNavPrimary={props.nav?.primaryLink}
          stickyNavSecondary={props.nav?.mobileStickyNavigationItems}
        />
      )}
    </>
  )
}

export default Page
